// Delad render-helper för /jamfor/<slug>-sidor (jämförelsekluster).
// Tar en Comparison och returnerar färdig route-config (head + component).
// Mönster: createFileRoute("/jamfor/<slug>")(comparisonRoute(getComparison("<slug>")))

import { Check, Minus } from "lucide-react";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { article, breadcrumbs, speakable, aeoGeo } from "@/lib/seo/schema";
import { CTA } from "@/lib/cta";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { lastmodFor } from "@/lib/seo/sitemap";
import { formatReviewed } from "@/lib/seo/reviewed";
import type { Comparison } from "@/lib/seo/comparisons";

const PUBLISHED = "2026-05-28";

export function comparisonRoute(c: Comparison) {
  const path = `/jamfor/${c.slug}`;
  const absUrl = `https://fenny.se${path}`;
  const reviewed = lastmodFor(path) ?? PUBLISHED;

  return {
    head: () => ({
      meta: [
        { title: c.metaTitle },
        { name: "description", content: c.metaDescription },
        { property: "og:title", content: c.metaTitle },
        { property: "og:description", content: c.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absUrl },
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path, name: c.metaTitle, description: c.metaDescription, dateModified: reviewed })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            article({
              headline: c.title,
              description: c.metaDescription,
              path,
              datePublished: PUBLISHED,
              dateModified: reviewed,
              authorName: "Fenny",
              section: "Jämförelse",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(toFaqJsonLd(c.faqIds)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Jämför", path: "/jamfor/pulse-vs-prime" },
              { name: c.breadcrumbLabel, path },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            speakable(["[data-quick-answer]", "h1"]),
          ),
        },
      ],
    }),
    component: () => <ComparisonPage c={c} path={path} reviewed={reviewed} />,
  };
}

function ComparisonPage({
  c,
  path,
  reviewed,
}: {
  c: Comparison;
  path: string;
  reviewed: string;
}) {
  return (
    <Section className="pt-32 pb-20">
      <div className="container-wide max-w-4xl">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {c.eyebrow}
        </p>
        <h1 className="mt-3 text-balance text-4xl font-semibold tracking-tight md:text-5xl">
          {c.title}
        </h1>

        <p className="mt-4 text-sm text-muted-foreground">
          Senast granskad {formatReviewed(reviewed)} av Fenny-redaktionen
        </p>

        <QuickAnswer text={c.shortAnswer} tone="mint" />

        <div className="mt-8 space-y-4 text-lg leading-relaxed text-muted-foreground">
          {c.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {c.kind === "competitor" && (
          <p className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-muted-foreground">
            Detta är en saklig jämförelse av modeller och arbetssätt — inga
            betyg, recensioner eller prisuppgifter om {c.otherName}. Stäm av
            aktuellt erbjudande direkt med {c.otherName} innan beslut.
          </p>
        )}

        {/* Jämförelsetabell */}
        <div className="mt-12 overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Dimension</th>
                <th className="px-4 py-3 text-left font-medium">Fenny</th>
                <th className="px-4 py-3 text-left font-medium">{c.otherName}</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((r) => (
                <tr key={r.dimension} className="border-t border-white/10">
                  <td className="px-4 py-3 font-medium text-foreground">
                    {r.dimension}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{r.fenny}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.other}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Beslutsstöd */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-mint/20 bg-mint/[0.04] p-6">
            <h2 className="text-lg font-semibold text-foreground">
              När Fenny passar bättre
            </h2>
            <ul className="mt-4 space-y-3">
              {c.fennyBetterWhen.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <h2 className="text-lg font-semibold text-foreground">
              När {c.otherName} passar bättre
            </h2>
            <ul className="mt-4 space-y-3">
              {c.otherBetterWhen.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                  <Minus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Så väljer du rätt */}
        <div className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">
            Så väljer du rätt
          </h2>
          <ol className="mt-6 space-y-4">
            {c.howToChoose.map((item, i) => (
              <li key={item} className="flex gap-4">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-mint/30 text-sm font-medium text-mint">
                  {i + 1}
                </span>
                <span className="pt-0.5 text-muted-foreground">{item}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Button to={CTA.priceFinder.to} variant="mint" arrow trackId={CTA.priceFinder.trackId} trackProps={{ location: "comparison_cta" }}>
            {CTA.priceFinder.label}
          </Button>
          <Button to={CTA.bookMeeting.to} variant="outline" arrow trackId={CTA.bookMeeting.trackId} trackProps={{ location: "comparison_cta" }}>
            {CTA.bookMeeting.label}
          </Button>
          <Button to="/filmabonnemang" variant="ghost" arrow>
            Om filmabonnemang
          </Button>
        </div>
      </div>

      {/* FAQ — samma svar som FAQPage JSON-LD */}
      <div className="container-wide max-w-4xl">
        <AnswerFaq
          answerIds={c.faqIds}
          eyebrow="Frågor & svar"
          title="Vanliga frågor"
          sectionId="faq"
          className="px-0 pt-16"
        />
        <RelatedLinksRail sourcePath={path} title="Relaterat" />
      </div>
    </Section>
  );
}

